// QuickSettingsDialog — modal wrapper for Quick Settings (AC2, AC7, AC14, AC15).
//
// Extends Dialog (Dialog.js + LoadDialog.js:121-129 pattern). The Dialog base renders its content
// once at construction; we re-render the content on open() with a bumped `revision` so the
// QuickSettingsContent rescans the (possibly changed) workspace, and again when the workspace is
// reset under us (AC15) — react-dom reconciles into the same node, so there is no duplicate
// jQuery dialog. We deliberately do NOT call registerCloseOnOtherDialog (it leaks globalObserver
// listeners — RFM4); close() removes the workspace listener we registered instead.
/* global Blockly */
import React from 'react';
import { render } from 'react-dom';
import Dialog from './Dialog';
import DialogComponent from './DialogComponent';
import * as style from '../style';
import { translate } from '../../../common/i18n';
import { observer as globalObserver } from '../../../common/utils/observer';
import QuickSettingsContent from '../quickSettings/QuickSettingsContent.jsx';
import { scanTunables } from '../quickSettings/scanner';
import { applyEdits } from '../quickSettings/apply';

const DIALOG_ID = 'quick-settings-dialog';

export default class QuickSettingsDialog extends Dialog {
    constructor() {
        // Pre-super closures (LoadDialog pattern): `this` is only accessed at call time, which is
        // always after construction completes.
        const scanner = () => scanTunables(Blockly.mainWorkspace);
        const onApply = edits => this.onApply(edits);
        const onClose = () => this.close();
        const onRun = () => this.onRun();

        super(
            DIALOG_ID,
            translate('Quick Settings'),
            <QuickSettingsContent scanner={scanner} onApply={onApply} onClose={onClose} onRun={onRun} revision={0} />,
            style.dialogLayout
        );

        this._scanner = scanner;
        this._revision = 0;
        this._onWsChange = ev => this.onWsChange(ev);
    }

    // Re-render the content into the dialog span with a fresh revision (forces a rescan).
    _renderContent() {
        const node = document.getElementById(DIALOG_ID);
        if (!node) {
            return;
        }
        render(
            <DialogComponent
                id={this.componentId}
                title={translate('Quick Settings')}
                options={style.dialogLayout}
                content={
                    <QuickSettingsContent
                        scanner={this._scanner}
                        onApply={edits => this.onApply(edits)}
                        onClose={() => this.close()}
                        onRun={() => this.onRun()}
                        revision={this._revision}
                    />
                }
            />,
            node
        );
    }

    open() {
        this._revision += 1;
        this._renderContent();
        if (Blockly.mainWorkspace) {
            Blockly.mainWorkspace.addChangeListener(this._onWsChange);
        }
        super.open();
    }

    // AC7 running guard: confirm before writing values while a bot is live; cancel leaves values
    // unchanged. Precedent: blocks/shared.js:452-469 reads globalObserver.getState('isRunning').
    // Kept as an instance method: called via `this.onApply(edits)` above and overridable per dialog.
    // eslint-disable-next-line class-methods-use-this
    onApply(edits) {
        if (globalObserver.getState('isRunning')) {
            const confirmed = typeof window !== 'undefined' && typeof window.confirm === 'function'
                ? window.confirm(translate('A bot is running. Apply Quick Settings anyway?'))
                : true;
            if (!confirmed) {
                return { applied: [], errors: [], banner: '' }; // cancel: no writes
            }
        }
        return applyEdits(Blockly.mainWorkspace, edits);
    }

    // Hands off to the real Run button's own click handler (View.js) — required-blocks / trade-limit
    // / reality-check / strategy-lock gates all live there, so this never bypasses them. Matches the
    // codebase's own re-trigger pattern for that handler (View.js: `$('#runButtonBottom').triggerHandler('click')`).
    // eslint-disable-next-line class-methods-use-this
    onRun() {
        $('#runButtonBottom').triggerHandler('click');
    }

    // AC15: a load/reset (grouped events) or a delete burst invalidates the open modal — bump the
    // revision so the content rescans and shows the stale banner when the workspace was cleared.
    onWsChange(ev) {
        if (!ev) {
            return;
        }
        if (ev.group === 'load' || ev.group === 'reset' || ev.type === Blockly.Events.DELETE) {
            this._revision += 1;
            this._renderContent();
        }
    }

    // RFM4 leak prevention: remove the listener we registered, then close. We do NOT call
    // registerCloseOnOtherDialog (Dialog.js:22-28) — it leaks globalObserver listeners.
    close() {
        if (Blockly.mainWorkspace) {
            Blockly.mainWorkspace.removeChangeListener(this._onWsChange);
        }
        super.close();
    }
}
